import type {
  GetListParams,
  GetListResult,
  CreateResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
  DeleteParams,
  DeleteResult,
} from "ra-core";
import { toRaClient, type ClientRaRecord } from "./Mappers";
import type { PageQuery } from "@/domain/port/PageQuery";
import type { ClientService } from "@/domain/port/ClientService";
import type { UpdateClientCommand } from "@/domain/model/Client";
import { asClientId } from "@/domain/model/Ids";

export function makeClientAdapter(clients: ClientService) {
  return {
    async getList(
      _resource: string,
      params: GetListParams,
    ): Promise<GetListResult<ClientRaRecord>> {
      const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
      const { field, order } = params.sort ?? {
        field: "createdAt",
        order: "DESC",
      };
      const filter = params.filter ?? {};
      const q: PageQuery = {
        page,
        pageSize: perPage,
        sortBy: field,
        sortDir: order.toLowerCase() as "asc" | "desc",
        filter,
      };
      const res = await clients.list(q);
      return { data: res.items.map(toRaClient), total: res.total };
    },

    async getOne(
      _resource: string,
      params: GetOneParams,
    ): Promise<GetOneResult<ClientRaRecord>> {
      const item = await clients.getById(asClientId(params.id));
      return { data: toRaClient(item) };
    },

    async create(
      _resource: string,
      { data }: { data: any },
    ): Promise<CreateResult<ClientRaRecord>> {
      const created = await clients.create(data);
      return { data: toRaClient(created) };
    },

    async update(
      _resource: string,
      params: UpdateParams,
    ): Promise<UpdateResult<ClientRaRecord>> {
      const updated = await clients.update(
        asClientId(params.id),
        params.data as UpdateClientCommand,
      );
      return { data: toRaClient(updated) };
    },

    async delete(
      _resource: string,
      params: DeleteParams,
    ): Promise<DeleteResult<ClientRaRecord>> {
      const deleted = await clients.delete(asClientId(params.id));
      return { data: toRaClient(deleted) };
      // If your backend returns nothing, you can return { data: params.previousData as any }
    },

    async getMany(
      _resource: string,
      params: GetManyParams,
    ): Promise<GetManyResult<ClientRaRecord>> {
      const ids = params.ids.map(asClientId);
      const items = await clients.getManyByIds(ids);
      return { data: items.map(toRaClient) };
    },
  };
}
